import { useCallback, useState } from "react";
import ReactDOM from "react-dom";

type TooltipOptions = {
  pointerPosition?: boolean;
  offsetX?: number;
  offsetY?: number;
};

interface TooltipData {
  x: number;
  y: number;
  text: string;
  visible: boolean;
}

export function useTooltip(options: TooltipOptions = {}) {
  const { pointerPosition = false } = options;

  const [tooltip, setTooltip] = useState<TooltipData>({
    x: 0,
    y: 0,
    text: "",
    visible: false,
  });

  const mouseEnterString = (str: string) => {
    setTooltip((prev) => ({
      ...prev,
      text: str,
      visible: true,
    }));
  };

  const mouseOut = () => {
    setTooltip((prev) => ({
      ...prev,
      visible: false,
    }));
  };

  const handle = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      const label = e.currentTarget.getAttribute("aria-label") ?? "";

      if (e.type === "mouseenter") {
        const x = pointerPosition
          ? e.clientX
          : e.currentTarget.getBoundingClientRect().left +
            e.currentTarget.getBoundingClientRect().width / 2;

        const y = pointerPosition
          ? e.clientY
          : e.currentTarget.getBoundingClientRect().top - 8;

        setTooltip({
          text: label,
          visible: true,
          x: pointerPosition
            ? e.clientX + (options.offsetX || 0)
            : e.currentTarget.getBoundingClientRect().left +
              e.currentTarget.getBoundingClientRect().width / 2,
          y: pointerPosition
            ? e.clientY + (options.offsetY || 0)
            : e.currentTarget.getBoundingClientRect().top - 8,
        });
      } else if (e.type === "mouseleave") {
        setTooltip((prev) => ({ ...prev, visible: false }));
      } else if (e.type === "mousemove" && pointerPosition) {
        setTooltip((prev) => ({
          ...prev,
          x: e.clientX + (options.offsetX || 0),
          y: e.clientY + (options.offsetY || 0),
        }));
      }
    },
    [pointerPosition],
  );

  const getTriggerProps = () => ({
    onMouseEnter: handle,
    onMouseLeave: handle,
    ...(pointerPosition ? { onMouseMove: handle } : {}),
  });

  const tooltipNode = tooltip.visible
    ? ReactDOM.createPortal(
        <div
          className={`tooltip ${pointerPosition ? "no-arrow" : "with-arrow"} fade-in`}
          style={{
            position: "fixed",
            top: tooltip.y,
            left: tooltip.x,
            transform: pointerPosition
              ? "translate(10px, 10px)"
              : "translate(-50%, -100%)",
            background: "#333",
            color: "#fff",
            padding: "6px 10px",
            borderRadius: "4px",
            fontSize: "0.75rem",
            pointerEvents: "none",
            zIndex: 9999,
            whiteSpace: "nowrap",
          }}
        >
          {tooltip.text}
        </div>,
        document.body,
      )
    : null;

  return {
    getTriggerProps,
    tooltipNode,
    mouseEnterString,
    mouseOut,
  };
}
